import { authenticatedInstance } from '../index/index.service';
import { extractServiceErrorMessage } from '../shared/service-error.util';
import type {
  UpdateWoltTenantConfigRequest,
  WoltCallLogsPage,
  WoltCallLogsQuery,
  WoltMenuSyncResult,
  WoltOnboardingLinkView,
} from 'shared-models/browser';

export type {
  UpdateWoltTenantConfigRequest,
  WoltCallDirection,
  WoltCallKind,
  WoltCallLogView,
  WoltCallLogsPage,
  WoltCallLogsQuery,
  WoltCallStatus,
  WoltMenuSyncResult,
  WoltOnboardingLinkView,
} from 'shared-models/browser';

class AdminWoltService {
  async list(query: WoltCallLogsQuery = {}): Promise<WoltCallLogsPage> {
    try {
      const response = await authenticatedInstance.get<WoltCallLogsPage>('/wolt/call-logs', { params: query });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'טעינת מעקב וולט נכשלה'));
    }
  }

  // Diff-only; the caller sends only changed fields, an empty diff never reaches here.
  async updateTenantConfig(tenantIdPk: number, payload: UpdateWoltTenantConfigRequest): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/wolt/tenants/${tenantIdPk}/config`, payload);
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'עדכון הגדרות וולט נכשל'));
    }
  }

  // Mints a tenant-bound self-service onboarding link; the merchant completes it on Wolt.
  async createOnboardingLink(tenantIdPk: number): Promise<WoltOnboardingLinkView> {
    try {
      const response = await authenticatedInstance.post<WoltOnboardingLinkView>(`/wolt/tenants/${tenantIdPk}/onboarding-link`);
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'יצירת קישור החיבור לוולט נכשלה'));
    }
  }

  async disconnect(tenantIdPk: number): Promise<void> {
    try {
      await authenticatedInstance.post<void>(`/wolt/tenants/${tenantIdPk}/disconnect`);
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'ניתוק וולט נכשל'));
    }
  }

  // Full-replace push of the tenant menu (super-admin); result = counts + skipped reasons.
  async syncMenu(tenantIdPk: number): Promise<WoltMenuSyncResult> {
    try {
      const response = await authenticatedInstance.post<WoltMenuSyncResult>(`/wolt/tenants/${tenantIdPk}/menu-sync`);
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, 'סנכרון התפריט לוולט נכשל'));
    }
  }
}

export const adminWoltService = new AdminWoltService();
